// src/components/SharedComponents.jsx
import React from 'react';
import { Box, Typography, Button, Chip as MuiChip, LinearProgress } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { getInitials } from '../util';

// ─── Section Header ───
export function SectionHeader({ title, subtitle, badge, badgeColor }) {
  return (
    <Box sx={{ mb: { xs: 4.5, sm: 5 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 0.75 }}>
        <Typography variant="h2" sx={{ fontSize: { xs: 31, sm: 36 }, lineHeight: 1.04, letterSpacing: '-1px', fontWeight: 850 }}>{title}</Typography>
        {badge && (
          <MuiChip
            label={badge}
            size="small"
            sx={{
              bgcolor: badgeColor || 'grey.100',
              color: 'text.secondary',
              fontWeight: 700,
              fontSize: 11,
              height: 24,
              borderRadius: '6px',
              px: 0.5,
            }}
          />
        )}
      </Box>
      {subtitle && (
        <Typography variant="body1" color="text.secondary" sx={{ fontSize: 15.5, lineHeight: 1.55, maxWidth: 760 }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

// ─── Upgrade Call-to-Action ───
export function UpgradeCTA({ onClick, text = 'Upgrade to add more' }) {
  return (
    <Button
      onClick={onClick}
      fullWidth
      startIcon={<AutoAwesomeIcon sx={{ fontSize: 18 }} />}
      sx={{
        py: 1.3,
        borderRadius: '6px',
        background: 'linear-gradient(135deg, #FFFCF0, #FFF8E1)',
        border: '1px solid #FFE082',
        color: 'warning.dark',
        fontWeight: 600,
        fontSize: 13,
        '&:hover': { background: 'linear-gradient(135deg, #FFF8E1, #FFECB3)' },
      }}
    >
      {text}
    </Button>
  );
}

// ─── Upgrade Lock Slot (for photo grids) ───
export function UpgradeLockSlot({ onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        aspectRatio: '1',
        borderRadius: '6px',
        background: 'linear-gradient(135deg, #FFFCF0, #FFF8E1)',
        border: '1px solid #FFE082',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        gap: 0.5,
        transition: 'transform 0.15s',
        '&:hover': { transform: 'scale(1.03)' },
      }}
    >
      <LockOutlinedIcon sx={{ fontSize: 22, color: '#FF9800' }} />
      <Typography sx={{ fontSize: 10, color: 'warning.dark', fontWeight: 600, textAlign: 'center', lineHeight: 1.2 }}>
        Upgrade
        <br />
        for more
      </Typography>
    </Box>
  );
}

// ─── Progress Ring ───
export function ProgressRing({ value, size = 44 }) {
  const r = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (value / 100) * circ;
  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#f0f0f0" strokeWidth={3.5} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="#3a3a40"
          strokeWidth={3.5}
          strokeDasharray={circ}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dashoffset 0.5s ease' }}
        />
      </svg>
      <Typography
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 11,
          fontWeight: 700,
        }}
      >
        {value}%
      </Typography>
    </Box>
  );
}

export function parseLocalDate(value) {
  if (!value) return null;

  const text = String(value).trim();

  // Treat anything beginning with YYYY-MM-DD as a local calendar deadline.
  // Covers:
  // 2026-06-07
  // 2026-06-07T00:00:00
  // 2026-06-07T00:00:00Z
  // 2026-06-07T00:00:00+00:00
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const [, year, month, day] = match;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  return new Date(text);
}

export function isDeadlinePassed(contentDeadline) {
  const deadline = parseLocalDate(contentDeadline);

  if (!deadline || Number.isNaN(deadline.getTime())) return false;

  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);

  const deadlineStart = new Date(deadline);
  deadlineStart.setHours(0, 0, 0, 0);

  return todayStart > deadlineStart;
}


// ─── Deadline Banner ───
export function DeadlineBanner({ bookConfig }) {
  if (!bookConfig?.content_deadline) return null;



  const deadline = parseLocalDate(bookConfig.content_deadline);

  if (!deadline || Number.isNaN(deadline.getTime())) return null;

  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);

  const deadlineStart = new Date(deadline);
  deadlineStart.setHours(0, 0, 0, 0);

  const daysLeft = Math.round((deadlineStart - todayStart) / 86400000);
  const isPassed = daysLeft < 0;
  const isToday = daysLeft === 0;
  const urgent = isPassed || isToday || daysLeft <= 7;
  const dayLabel = daysLeft === 1 ? 'day' : 'days';

  const title = isPassed
    ? 'Deadline passed'
    : isToday
      ? 'Last day to submit'
      : urgent
        ? `Only ${daysLeft} ${dayLabel} left!`
        : `${daysLeft} ${dayLabel} until book closes`;

  const subtitle = isPassed
    ? 'Submissions are locked for printing.'
    : `Submission deadline: ${deadline.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })}`;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 2,
        borderRadius: 3,
        background: isPassed
          ? 'linear-gradient(135deg, #F5F5F5, #ECECEC)'
          : urgent
            ? 'linear-gradient(135deg, #FFF0F0, #FFE0E0)'
            : 'linear-gradient(135deg, #F0F7FF, #E0EDFF)',
        border: `1px solid ${isPassed ? '#D0D0D0' : urgent ? '#FFB3B3' : '#B3D4FF'}`,
        mb: 3.5,
      }}
    >
      <Typography sx={{ fontSize: 22 }}>
        {isPassed ? '🔒' : urgent ? '⏰' : '📅'}
      </Typography>

      <Box sx={{ flex: 1 }}>
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 600,
            color: isPassed ? 'text.secondary' : urgent ? 'error.main' : 'info.main',
          }}
        >
          {title}
        </Typography>

        <Typography
          sx={{
            fontSize: 12,
            color: isPassed ? 'text.secondary' : urgent ? '#E57373' : '#64B5F6',
          }}
        >
          {subtitle}
        </Typography>
      </Box>
    </Box>
  );
}

// ─── Child Avatar ───
export function ChildAvatar({ url, first_name, last_name, gender, size = 30 }) {
  const bg = gender === '0' ? '#FFD6E0' : '#D6E4FF'; // Note: Use === for strict equality

  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        bgcolor: bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * 0.43,
        fontWeight: 600,
        color: 'text.primary',
        flexShrink: 0,
      }}
    >
      {url ? (
        <img
          src={url}
          alt={`${first_name} ${last_name}`} // Better accessibility
          style={{
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            objectFit: 'cover' // Ensures the image doesn't stretch/squish
          }}
        />
      ) : (
        getInitials(`${first_name} ${last_name}`)
      )}
    </Box>
  );
}

// ─── Memory Chip ───
export function MemoryChip({ label, active, onClick, icon }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.6,
        px: 1.25,
        py: 0.78,
        borderRadius: '8px',
        border: '1px solid',
        borderColor: active ? 'grey.700' : 'grey.400',
        bgcolor: 'background.paper',
        color: 'text.primary',
        fontSize: 14,
        fontWeight: active ? 750 : 600,
        lineHeight: 1,
        cursor: 'pointer',
        font: 'inherit',
        appearance: 'none',
        WebkitAppearance: 'none',
        boxShadow: 'none',
        transition: 'border-color 140ms ease, background-color 140ms ease',
        '&:hover': {
          borderColor: active ? 'grey.800' : 'grey.500',
          bgcolor: active ? 'rgba(0,0,0,0.03)' : 'grey.50',
        },
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: 2,
        },
      }}
    >
      {active && (
        <Box
          component="span"
          sx={{
            fontSize: 12,
            lineHeight: 1,
            color: 'text.primary',
            fontWeight: 800,
          }}
        >
          ✓
        </Box>
      )}
      {icon}
      {label}
    </Box>
  );
}